<template>
  <div id="settlement-list-list-view" class="datatable-container">
    <vs-table ref="table" :data="advisorSettlements">
      <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between mb-4">
        <div class="flex flex-wrap-reverse  data-list-btn-container">
          <date-picker v-model="dateRange" range class="mr-2"></date-picker>
          <v-select v-model="statusField" :options="statusOptions" label="label" class="mr-2"></v-select>
        </div>

        <vs-input
          icon-no-border
          placeholder="Search"
          v-model="search"
          class="input-rounded-full"
          icon="icon-search"
          icon-pack="feather"
        />
      </div>

      <template slot="thead">
        <vs-th sort-key="advisorName">Advisor Name</vs-th>
        <vs-th sort-key="amount">Amount</vs-th>
        <vs-th sort-key="fromDate">From Date</vs-th>
        <vs-th sort-key="toDate">To Date</vs-th>
        <vs-th sort-key="createdAt">Created At</vs-th>
        <vs-th sort-key="status">Status</vs-th>
        <vs-th>Action</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tbody>
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>{{ tr.advisor.name }}</vs-td>

            <vs-td>{{ tr.currency }} {{ Number(tr.amount || 0).toFixed(2) }}</vs-td>

            <vs-td>{{ tr.fromDate | moment('DD MMM YYYY, hh:mm A') }}</vs-td>

            <vs-td>{{ tr.toDate | moment('DD MMM YYYY, hh:mm A') }}</vs-td>

            <vs-td>{{ tr.createdAt | moment('DD MMM YYYY, hh:mm A') }}</vs-td>

            <vs-td>
              <vs-chip :color="getStatusColor(tr.status)">
                {{ getStatusLabel(tr.status) }}
              </vs-chip>
            </vs-td>

            <vs-td class="whitespace-no-wrap">
              <feather-icon
                icon="EditIcon"
                svgClasses="w-5 h-5 hover:text-primary stroke-current mr-2"
                @click.stop="handleEdit(tr)" />
              <feather-icon
                icon="DownloadIcon"
                svgClasses="w-5 h-5 hover:text-primary stroke-current mr-2"
                @click.stop="handleDocumentClick(tr.attachmentUrl)" />
            </vs-td>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>

    <Pagination :total="totalPage" v-model="currentPage" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Pagination from '@/components/Pagination';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/en';
import _ from 'lodash';
import moment from 'moment';

export default {
  components: {
    Pagination,
    DatePicker,
  },
  data() {
    return {
      dateRange: [],
      status: '',
      workflowStatus: '',
      itemsPerPage: 10,
      currentPage: 1,
      pageTotal: 0,
      totalItem: 0,
      totalPage: 0,
      filter: {},
      filterQuery: '',
      showPopup: false,
      showAssessmentReviewPopup: false,
      popupContent: null,
      sorting: 'createdAt,DESC',
      search: '',
    };
  },
  computed: {
    ...mapState({
      advisorSettlements: ({ advisorSettlement }) => advisorSettlement.advisorSettlements,
    }),
    statusOptions() {
      return [
        { label: 'ALL', value: '' },
        { label: 'PENDING', value: 'PENDING' },
        { label: 'APPROVED', value: 'APPROVED' },
        { label: 'REJECT', value: 'REJECT' },
        { label: 'PAID', value: 'PAID' },
      ];
    },
    statusField: {
      get() {
        const status = this.statusOptions.find(x => x.value == this.status);
        return status ? { label: status.label, value: this.status } : null;
      },
      set(val) {
        this.status = val.value;
      }
    },
    orderDisplayColumns() {
      return [
        { label: 'Amount', key: 'settlementOrder.displayAmount', isDate: false },
        { label: 'Advisor Name', key: 'advisor.name', isDate: false },
        { label: 'Settlement Date Time', key: 'startDatetime', isDate: true },
        { label: 'Platform', key: 'settlementOrder.platform', isDate: false },
        { label: 'Remarks', key: 'remarks', isDate: false },
        { label: 'Created At', key: 'settlementOrder.createdAt', isDate: true },
      ]
    },
    getStatusLabel() {
      return value => _.find(this.statusOptions, { value }).label;
    },
    queryParams() {
      return {
        page: this.currentPage,
        limit: this.itemsPerPage,
        filter: [..._.values(this.filter)],
        sort: this.sorting
      };
    },
    getKeyValueFromContent() {
      return key => _.get(this.popupContent, key);
    },
  },
  methods: {
    ...mapActions({
      _getAdvisorSettlements: 'advisorSettlement/getAdvisorSettlements'
    }),
    getStatusColor(status) {
      if (status === 'PENDING') return 'warning';
      if (status === 'REJECT') return 'danger';
      if (status === 'APPROVED') return 'success';
      if (status === 'PAID') return 'primary';
      return 'primary';
    },
    getAdvisorSettlements(params) {
      this._getAdvisorSettlements(params)
        .then(response => {
          this.currentPage = parseInt(response.headers['x-pagination-page']);
          this.pageTotal = parseInt(response.headers['x-pagination-count']);
          this.totalItem = parseInt(response.headers['x-pagination-total']);
          this.totalPage = parseInt(response.headers['x-pagination-page-count']);
        })
        .catch(err => {
          console.error({ err });
        });
    },
    handleEdit(settlement) {
      this.$router.push({ name: 'advisor-settlement-detail', params: { id: settlement.id }});
    },
    handleDocumentClick(url) {
      window.open(url, '_blank');
    }
  },
  async mounted() {
    this.getAdvisorSettlements({
      page: this.currentPage,
      limit: this.itemsPerPage,
      sort: this.sorting
    });
  },
  watch: {
    dateRange() {
      if(this.dateRange[0] && this.dateRange[1]) {
        const field = 'fromDate';
        const end = moment(this.dateRange[1]).format('YYYY-MM-DD 23:59:59');

        this.$set(this.filter, 'dateRangeStart', `${field}||$gte||${this.dateRange[0].toISOString()}}`);
        this.$set(this.filter, 'dateRangeEnd', `${field}||$lte||${moment(end).toISOString()}`);
      } else {
        this.$delete(this.filter, 'dateRangeStart');
        this.$delete(this.filter, 'dateRangeEnd');
      }

      return this.getAdvisorSettlements(this.queryParams);
    },
    status() {
      if(this.status) {
        this.$set(this.filter, 'status', `status||$eq||${this.status}`);
      } else {
        this.$delete(this.filter, 'status');
      }
      return this.getAdvisorSettlements(this.queryParams);
    },
    async currentPage(newValue, oldValue) {
      if (newValue !== oldValue) {
        await this.getAdvisorSettlements(this.queryParams);
      }
    },
    search: _.debounce(async function(newValue, oldValue) {
      if (newValue !== oldValue) {
        let params = this.queryParams;
        if (newValue) {
          params = {
            ...params,
            page: 1, // back to first page upon searching
            or: [`advisor.name||$eq||${this.search}`],
          };
        }
        this.getAdvisorSettlements(params);
      }
    }, 1000),
  },
};
</script>
<style lang="scss">
#settlement-list-list-view {
  .mx-input {
    height: auto;
    font-size: 1rem;
    line-height: 1.5;
    letter-spacing: 0.01rem;
    padding: 8px;
  }
}
.con-vs-popup {
  z-index: 52003;
}
.v-select .vs__dropdown-toggle {
  padding: 6px 0 8px;
  min-width: 240px;
  background: white;
}
</style>
